import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { ACCESS_GROUPS, hasAccessGroup, hasAppAudience } from "../../access-control/models/access-groups.models";
import { ADMIN_FEATURE_KEYS, FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { useAdminKeysSecretsPendingRotationStatus } from "../../admin/hooks/useAdminKeysSecretsPendingRotationStatus";
import { ADMIN_PATHS } from "../../admin/models/admin.models";
import { useAuthContext } from "../../auth/state/AuthContext";
import { canAccessPbiDashboards } from "../../pbi/hull-cleaning/utils/hull-cleaning-dashboard-access";
import { pbiMockDataEnabledAtom } from "../../pbi/state/pbi.atoms";
import { canAccessVesselCard } from "../../pbi/vessel-card/utils/vessel-card-access.utils";
import CargillLogo from "../../shared/assets/CargillLogo.svg";
import CargillLogoNight from "../../shared/assets/CargillLogoNight.svg";
import { DocumentIcon, ShipCircleIcon, PaperWithCheckMarkIcon, SpeedometerIcon, SweepIcon } from "../Icons";
import { featureFlagsAtom, hasPendingKeysSecretsRotationAtom, isDarkModeAtom, themeAtom } from "../atoms";
import { Popover } from "@headlessui/react";
import {
  ArrowsRightLeftIcon,
  CalculatorIcon,
  ChartBarSquareIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PresentationChartLineIcon,
  SwatchIcon,
  TableCellsIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { useAtom, useAtomValue } from "jotai";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const ADMIN_LINK_LABEL = "Admin";
const ADMIN_LINK_PENDING_ROTATION_LABEL = "Admin, Keys and Secrets rotation pending";

// Keeps the migrated Power BI pages discoverable from one compact Sidebar menu.
const PBI_LINKS = [
  {
    path: "/vessel-searcher",
    label: "Vessel Searcher",
    icon: MagnifyingGlassIcon,
    testId: "sidebar-vessel-searcher-link",
  },
  { path: "/vessel-data", label: "Vessel Data", icon: TableCellsIcon, testId: "sidebar-vessel-data-link" },
  {
    path: "/tf-hull-evaluations",
    label: "Tech Forms and Hull Evaluation",
    icon: ClipboardDocumentCheckIcon,
    testId: "sidebar-tech-forms-hull-evaluation-link",
  },
  {
    path: "/vessel-evaluation-follower",
    label: "Vessel Evaluation Follower",
    icon: PresentationChartLineIcon,
    testId: "sidebar-vessel-evaluation-follower-link",
  },
  {
    path: "/voyage-evaluation-follower",
    label: "Voyage Evaluation Follower",
    icon: ClipboardDocumentCheckIcon,
    testId: "sidebar-voyage-evaluation-follower-link",
  },
  {
    path: "/sister-vessel-search",
    label: "Sister Searcher",
    icon: UsersIcon,
    testId: "sidebar-sister-vessel-search-link",
  },
  {
    path: "/hull-paints-info-active-vessels",
    label: "Hull Paints",
    icon: SwatchIcon,
    testId: "sidebar-hull-paints-info-link",
  },
  {
    path: "/hull-cleaning-follower",
    label: "Hull Cleaning Follower",
    icon: WrenchScrewdriverIcon,
    testId: "sidebar-hull-cleaning-follower-link",
  },
  { path: "/calculators", label: "Calculators", icon: CalculatorIcon, testId: "sidebar-calculators-link" },
] as const;

// Keeps Admin at the bottom of the viewport while the main links scroll on shorter screens.
// The Power BI popover handles outside clicks, Escape, and focus restoration through Headless UI.
const Sidebar = ({ className }: { className?: string }) => {
  const location = useLocation();
  const isHome = location.pathname.includes("/home");
  const isLandingPage = location.pathname.includes("/landingPage");
  const isSearchVessels = location.pathname.includes("/vessels");
  const isHoldCleaning = location.pathname.includes("/hold-cleaning");
  const isCertificates = location.pathname.includes("/certificates");
  const isReviewPnl = location.pathname.includes("/voyage/pnlList");
  const isVesselCard = location.pathname.includes("/vessel-card");
  const isPbiPage =
    PBI_LINKS.some(({ path }) => location.pathname.includes(path)) ||
    location.pathname.includes("/tech-forms-hull-evaluation");
  const isVesselClustering = location.pathname.startsWith("/vessel-clustering");
  const isAdminPage = location.pathname.includes(ADMIN_PATHS.root);
  const featureFlags = useAtomValue(featureFlagsAtom);
  const theme = useAtomValue(themeAtom);
  const { hasFeature, hasAnyFeature } = useFeatureAccess();
  const hasPendingKeysSecretsRotation = useAtomValue(hasPendingKeysSecretsRotationAtom);

  const user = useAuthContext().state.user;
  const accessToken = user?.accessToken;
  const groups = accessToken?.groups ?? [];
  const canViewPbiDashboards = [accessToken?.email, accessToken?.sub].some(canAccessPbiDashboards);
  const [pbiMockDataEnabled, setPbiMockDataEnabled] = useAtom(pbiMockDataEnabledAtom);
  const isDarkMode = useAtomValue(isDarkModeAtom);

  const hasAudience = hasAppAudience(groups);
  const canViewVesselCard = hasAudience && canAccessVesselCard(accessToken, user?.accessControl);
  const canViewSistership = hasAudience && hasFeature(FEATURE_KEYS.SISTERSHIP_ACCESS);
  const isInternal = hasAccessGroup(groups, ACCESS_GROUPS.InternalUsers);
  const isExternal = hasAccessGroup(groups, ACCESS_GROUPS.ExternalUsers);
  const isAdmin = hasAccessGroup(groups, ACCESS_GROUPS.Admin);
  const canViewHome = isInternal && hasFeature(FEATURE_KEYS.CORE_MY_VESSELS);
  const canSearchVessels = isInternal && hasFeature(FEATURE_KEYS.CORE_SEARCH_VESSELS);
  const canViewHoldCleaning =
    featureFlags.hold_cleaning_intelligence_enabled && isInternal && hasFeature(FEATURE_KEYS.HOLD_CLEANING_ACCESS);
  const canViewCertificates = isInternal && hasFeature(FEATURE_KEYS.CORE_CERTS);
  const canReviewPnl = isInternal && hasFeature(FEATURE_KEYS.REVIEW_PNL);
  const canViewBqTqAdmin =
    hasFeature(FEATURE_KEYS.TQ_ADMIN) &&
    (hasAccessGroup(groups, ACCESS_GROUPS.Admin) || hasAccessGroup(groups, ACCESS_GROUPS.VesselPerformance));
  const canViewAdmin = hasAudience && (hasAnyFeature(ADMIN_FEATURE_KEYS) || canViewBqTqAdmin || isAdmin);
  useAdminKeysSecretsPendingRotationStatus(hasAudience && isAdmin);

  const activeLinkStyle: React.CSSProperties = {
    borderRadius: "8px",
    background: theme.sideBarComponent.background.activeLink,
    transition: "background 0.3s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px",
    color: theme.sideBarComponent.iconAndText.active,
    width: "52px",
    height: "52px",
  };

  const linkStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px",
    width: "52px",
    height: "52px",
  };

  // The svg of the vessel icon contains unwanted paddings.
  // These paddings force us to make the vessel icon bigger.
  // To fix the sidebar to bring it back to design specs I had to use this trick.
  // Idally should be fixed by exporting the icon with proper padding.
  const patchVesselIcon: React.CSSProperties = { margin: "-4px" };
  const adminLinkLabel = hasPendingKeysSecretsRotation ? ADMIN_LINK_PENDING_ROTATION_LABEL : ADMIN_LINK_LABEL;

  return (
    <div
      data-test="app-sidebar"
      className={twMerge(
        "sticky top-0 flex h-[100dvh] flex-shrink-0 flex-col overflow-visible border-r-[1px]",
        theme.sideBarComponent.tailWind.greyscale20,
        theme.sideBarComponent.tailWind.white,
        className,
      )}
      style={{ zIndex: "100" }}
    >
      <Tooltip
        id="sidebar-tooltip"
        place="right"
        disableStyleInjection={true}
        style={{
          color: theme.sideBarComponent.iconAndText.tooltipText,
          background: theme.sideBarComponent.background.tooltip,
          borderRadius: "8px",
          position: "absolute",
          right: "0",
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col p-3 pt-6">
        <ul className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:shrink-0">
          <li className="mb-[45px]">
            {isDarkMode ? (
              <img src={CargillLogoNight} alt="Icon" width={"53px"} height={"35px"} className="p-20px" />
            ) : (
              <img src={CargillLogo} alt="Icon" width={"53px"} height={"35px"} className="p-20px" />
            )}
          </li>

          {(canViewHome ||
            canSearchVessels ||
            canViewHoldCleaning ||
            canViewCertificates ||
            canReviewPnl ||
            canViewPbiDashboards ||
            hasAudience) && (
            <>
              {canViewHome && (
                <li className="mb-6 flex items-center">
                  <Link to="/home" style={isHome ? activeLinkStyle : linkStyle}>
                    <HomeIcon
                      className="hover:#1F8475 h-6 w-6 flex-shrink-0 stroke-[1.85] no-underline outline-none"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Home"
                      color={
                        isHome ? theme.sideBarComponent.iconAndText.active : theme.sideBarComponent.iconAndText.inactive
                      }
                    />
                  </Link>
                </li>
              )}
              {canSearchVessels && (
                <li className="mb-2 flex items-center">
                  <Link to="/vessels" style={isSearchVessels ? activeLinkStyle : linkStyle}>
                    <ShipCircleIcon
                      className="h-10 w-10 no-underline outline-none"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Vessels"
                      color={
                        isSearchVessels
                          ? theme.sideBarComponent.iconAndText.active
                          : theme.sideBarComponent.iconAndText.inactive
                      }
                      style={patchVesselIcon}
                    />
                  </Link>
                </li>
              )}
              {canViewSistership && (
                <li className="mb-6 flex items-center">
                  <Link
                    to="/vessel-clustering"
                    aria-label="Sistership"
                    data-test="sidebar-sistership-link"
                    style={isVesselClustering ? activeLinkStyle : linkStyle}
                  >
                    <ArrowsRightLeftIcon
                      className="h-6 w-6 flex-shrink-0 stroke-[1.85]"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Sistership"
                      color={
                        isVesselClustering
                          ? theme.sideBarComponent.iconAndText.active
                          : theme.sideBarComponent.iconAndText.inactive
                      }
                    />
                  </Link>
                </li>
              )}
              {featureFlags?.certificates_enabled && canViewCertificates && (
                <li className="mb-6 flex items-center">
                  <Link to="/certificates" style={isCertificates ? activeLinkStyle : linkStyle}>
                    <DocumentIcon
                      className="h-6 w-6 no-underline outline-none"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Certificates"
                      color={
                        isCertificates
                          ? theme.sideBarComponent.iconAndText.active
                          : theme.sideBarComponent.iconAndText.inactive
                      }
                    />
                  </Link>
                </li>
              )}
              {canReviewPnl && (
                <li className="mb-6 flex items-center">
                  <Link to="/voyage/pnlList" style={isReviewPnl ? activeLinkStyle : linkStyle}>
                    <PaperWithCheckMarkIcon
                      className="h-6 w-6 no-underline outline-none"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Review PnL"
                      color={
                        isReviewPnl
                          ? theme.sideBarComponent.iconAndText.active
                          : theme.sideBarComponent.iconAndText.inactive
                      }
                      style={patchVesselIcon}
                    />
                  </Link>
                </li>
              )}
              {canViewHoldCleaning && (
                <li className="mb-6 flex items-center">
                  <Link to="/hold-cleaning" style={isHoldCleaning ? activeLinkStyle : linkStyle}>
                    <SweepIcon
                      className="h-6 w-6 no-underline outline-none"
                      data-tooltip-id="sidebar-tooltip"
                      data-tooltip-html="Hold Cleaning"
                      color={
                        isHoldCleaning
                          ? theme.sideBarComponent.iconAndText.active
                          : theme.sideBarComponent.iconAndText.inactive
                      }
                    />
                  </Link>
                </li>
              )}
            </>
          )}

          {canViewVesselCard && (
            <li className="mb-6 flex items-center">
              <Link
                to="/vessel-card"
                style={isVesselCard ? activeLinkStyle : linkStyle}
                aria-label="Vessel Card"
                data-test="sidebar-vessel-card-link"
              >
                <SpeedometerIcon
                  className="h-7 w-7 flex-shrink-0 stroke-[1.85] no-underline outline-none"
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-html="Vessel Card"
                  color={
                    isVesselCard
                      ? theme.sideBarComponent.iconAndText.active
                      : theme.sideBarComponent.iconAndText.inactive
                  }
                />
              </Link>
            </li>
          )}

          {isInternal && canViewPbiDashboards && (
            <li className="relative mb-6 flex items-center">
              <Popover data-test="sidebar-pbi-menu">
                <Popover.Button
                  aria-label="Power BI pages"
                  data-test="sidebar-pbi-menu-button"
                  className="cursor-pointer"
                  style={isPbiPage ? activeLinkStyle : linkStyle}
                >
                  <ChartBarSquareIcon
                    className="h-6 w-6 flex-shrink-0 stroke-[1.85] no-underline outline-none"
                    data-tooltip-id="sidebar-tooltip"
                    data-tooltip-html="Power BI pages"
                    color={
                      isPbiPage
                        ? theme.sideBarComponent.iconAndText.active
                        : theme.sideBarComponent.iconAndText.inactive
                    }
                  />
                  <ChevronDownIcon className="ml-0.5 h-3 w-3" aria-hidden="true" />
                </Popover.Button>

                <Popover.Panel
                  data-test="sidebar-pbi-menu-panel"
                  className="fixed bottom-6 left-[76px] z-[200] flex max-h-[calc(100dvh-3rem)] w-80 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
                >
                  {({ close }) => (
                    <>
                      <div className="shrink-0 border-b border-gray-200 p-4 dark:border-gray-700">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Data source
                            </p>
                            <p className="text-sm font-medium">
                              {pbiMockDataEnabled ? "Modeled mock data" : "Real Snowflake data"}
                            </p>
                          </div>
                          <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold">
                            <span>Real</span>
                            <input
                              type="checkbox"
                              checked={pbiMockDataEnabled}
                              onChange={(event) => setPbiMockDataEnabled(event.target.checked)}
                              aria-label="Use mock data for Power BI pages"
                              data-test="sidebar-pbi-mock-toggle"
                              className="peer sr-only"
                            />
                            <span
                              className="relative h-6 w-11 rounded-full bg-gray-300 transition after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-green peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-green peer-focus-visible:ring-offset-2"
                              aria-hidden="true"
                            />
                            <span>Mock</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          The selection applies to every page in this menu and resets to Real after a reload.
                        </p>
                      </div>

                      <nav className="max-h-[min(70vh,520px)] min-h-0 overflow-y-auto p-2" aria-label="Power BI pages">
                        {PBI_LINKS.map(({ path, label, icon: Icon, testId }) => {
                          const isActive =
                            location.pathname.includes(path) ||
                            (path === "/tf-hull-evaluations" &&
                              location.pathname.includes("/tech-forms-hull-evaluation"));

                          return (
                            <Link
                              key={path}
                              to={path}
                              onClick={() => close()}
                              data-test={testId}
                              className={twMerge(
                                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-primary-10 hover:text-green dark:text-gray-200 dark:hover:bg-gray-700",
                                isActive && "bg-primary-10 text-green dark:bg-gray-700",
                              )}
                            >
                              <Icon className="h-5 w-5 flex-none" aria-hidden="true" />
                              <span>{label}</span>
                            </Link>
                          );
                        })}
                      </nav>
                    </>
                  )}
                </Popover.Panel>
              </Popover>
            </li>
          )}

          {/* External */}
          {isExternal && (
            <li className="mb-6 flex items-center">
              <Link to="/landingPage" style={isLandingPage ? activeLinkStyle : linkStyle}>
                <HomeIcon
                  className="h-6 w-6 flex-shrink-0 stroke-[1.85] no-underline outline-none"
                  color={
                    isLandingPage
                      ? theme.sideBarComponent.iconAndText.active
                      : theme.sideBarComponent.iconAndText.inactive
                  }
                  aria-hidden="true"
                />
              </Link>
            </li>
          )}
        </ul>

        {canViewAdmin && (
          <ul className="mt-auto flex shrink-0 flex-col items-center pt-3" data-test="sidebar-bottom-navigation">
            <li className="flex items-center pb-3">
              <Link
                to={ADMIN_PATHS.root}
                style={isAdminPage ? activeLinkStyle : linkStyle}
                aria-label={adminLinkLabel}
                data-test="sidebar-admin-link"
                className="relative"
              >
                <Cog6ToothIcon
                  className="h-6 w-6 flex-shrink-0 stroke-[1.85] no-underline outline-none"
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-html={adminLinkLabel}
                  color={
                    isAdminPage
                      ? theme.sideBarComponent.iconAndText.active
                      : theme.sideBarComponent.iconAndText.inactive
                  }
                />
                {hasPendingKeysSecretsRotation && (
                  <span
                    aria-hidden="true"
                    data-test="sidebar-admin-keys-secrets-dot"
                    className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-[#BF0002] ring-2 ring-white dark:ring-gray-800"
                  />
                )}
              </Link>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
